import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Team, TeamMember, TeamRole } from "../domain/team.types";

export type TeamsStatus = "idle" | "loading" | "succeeded" | "failed";

export interface TeamsState {
  teams: Team[];
  status: TeamsStatus;
  error: string | null;
  /** Members keyed by teamId, loaded lazily per team detail visit. */
  members: Record<string, TeamMember[]>;
  membersStatus: TeamsStatus;
  membersError: string | null;
  /** Every team + role the current user holds, across all teams - used by the Dashboard. */
  myMemberships: TeamMember[];
  myMembershipsStatus: TeamsStatus;
  myMembershipsError: string | null;
}

const initialState: TeamsState = {
  teams: [],
  status: "idle",
  error: null,
  members: {},
  membersStatus: "idle",
  membersError: null,
  myMemberships: [],
  myMembershipsStatus: "idle",
  myMembershipsError: null,
};

const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    teamsRequestStarted(state) {
      state.status = "loading";
      state.error = null;
    },
    teamsLoaded(state, action: PayloadAction<Team[]>) {
      state.teams = action.payload;
      state.status = "succeeded";
    },
    teamsRequestFailed(state, action: PayloadAction<string>) {
      state.status = "failed";
      state.error = action.payload;
    },
    teamAdded(state, action: PayloadAction<Team>) {
      state.teams.unshift(action.payload);
    },
    teamUpdated(state, action: PayloadAction<Team>) {
      const index = state.teams.findIndex((team) => team.id === action.payload.id);
      if (index !== -1) state.teams[index] = action.payload;
    },
    membersRequestStarted(state) {
      state.membersStatus = "loading";
      state.membersError = null;
    },
    membersLoaded(state, action: PayloadAction<{ teamId: string; members: TeamMember[] }>) {
      state.members[action.payload.teamId] = action.payload.members;
      state.membersStatus = "succeeded";
    },
    membersRequestFailed(state, action: PayloadAction<string>) {
      state.membersStatus = "failed";
      state.membersError = action.payload;
    },
    memberAdded(state, action: PayloadAction<TeamMember>) {
      const list = state.members[action.payload.teamId] ?? [];
      state.members[action.payload.teamId] = [...list, action.payload];
    },
    memberRemoved(
      state,
      action: PayloadAction<{ teamId: string; userId: string; role: TeamRole }>,
    ) {
      const list = state.members[action.payload.teamId] ?? [];
      state.members[action.payload.teamId] = list.filter(
        (member) =>
          !(member.userId === action.payload.userId && member.role === action.payload.role),
      );
    },
    myMembershipsRequestStarted(state) {
      state.myMembershipsStatus = "loading";
      state.myMembershipsError = null;
    },
    myMembershipsLoaded(state, action: PayloadAction<TeamMember[]>) {
      state.myMemberships = action.payload;
      state.myMembershipsStatus = "succeeded";
    },
    myMembershipsRequestFailed(state, action: PayloadAction<string>) {
      state.myMembershipsStatus = "failed";
      state.myMembershipsError = action.payload;
    },
  },
});

export const {
  teamsRequestStarted,
  teamsLoaded,
  teamsRequestFailed,
  teamAdded,
  teamUpdated,
  membersRequestStarted,
  membersLoaded,
  membersRequestFailed,
  memberAdded,
  memberRemoved,
  myMembershipsRequestStarted,
  myMembershipsLoaded,
  myMembershipsRequestFailed,
} = teamsSlice.actions;

export default teamsSlice.reducer;
